import { useCallback, useState } from 'react';
import {
  getDefaultSingleTypeQuestionOptions,
  getDefaultTableData,
} from '../../../constants/defaultValues';
import { useAppConfig, useUpdateAppConfig } from '../../../queries/useAppConfig';
import { ExclusionCategory, QUESTION_TYPE, SurveyQuestion, SurveySection } from '../../../types';
import { useSortedExclusionCategories } from '../../../hooks/useSortedExclusionCategories';
import { useUpdateExclusionsScreeningSurvey } from '../../../queries/useSurveys';
import { useToastMessage } from '../../../hooks/useToastMessage';
import {
  useCreateExclusionsDbScreenshot,
  useResetExclusionsDbTablesFromScreenshot,
} from '../../../queries/useExclusions';

export const useExclusionsConfirmBarActionsSurvey = () => {
  const { items } = useSortedExclusionCategories();
  const { data: appConfigData } = useAppConfig();
  const updateAppConfig = useUpdateAppConfig();
  const createExclusionsDbScreenshot = useCreateExclusionsDbScreenshot();
  const resetFromScreenshot = useResetExclusionsDbTablesFromScreenshot();
  const [areControlsDisabled, setAreControlsDisabled] = useState(false);

  const updateExclusionsSurvey = useUpdateExclusionsScreeningSurvey();
  const { pushSuccessToast, pushErrorToast } = useToastMessage();

  const getQuestions = (category: ExclusionCategory): SurveyQuestion[] => {
    return category.conditions
      ?.filter(
        (condition) =>
          condition?.isActive && condition?.exclusions?.some((exclusion) => exclusion?.isActive)
      )
      ?.map((condition) => {
        return {
          id: String(condition.id),
          type: QUESTION_TYPE.SINGLE,
          options: getDefaultSingleTypeQuestionOptions(),
          tableData: getDefaultTableData(),
          includeInstructions: false,
          includeEvaluation: false,
          evaluationRules: null,
          instructions: '',
          sectionId: String(category.id),
          isActive: condition.isActive,
          isRequired: true,
          name: condition.condition,
          followUpQuestions: condition.exclusions
            ?.filter((exclusion) => exclusion?.isActive)
            ?.map((exclusion) => {
              return {
                id: String(exclusion?.id),
                name: exclusion?.exclusion,
                type: QUESTION_TYPE.SINGLE,
                options: getDefaultSingleTypeQuestionOptions(),
                tableData: getDefaultTableData(),
                includeInstructions: false,
                includeEvaluation: false,
                evaluationRules: null,
                instructions: '',
                sectionId: String(condition?.id),
                isActive: exclusion.isActive,
                condition: condition.condition,
                isRequired: true,
                investorId: Number(exclusion.externalInvestorId),
                funds: exclusion?.externalFunds?.map((fund) => fund.id),
              };
            }),
        };
      });
  };

  const onUpdateSurvey = useCallback(async () => {
    setAreControlsDisabled(true);
    createExclusionsDbScreenshot.mutate();
    try {
      const activeCategories =
        items?.filter((item) =>
          item.conditions?.some(
            (condition) =>
              condition?.isActive && condition?.exclusions?.some((exclusion) => exclusion?.isActive)
          )
        ) || [];

      const sections: SurveySection[] = activeCategories?.map((item) => {
        return {
          id: String(item.id),
          name: item.name,
          collapsed: false,
          depth: 0,
          parentId: null,
          subSections: [],
          questions: getQuestions(item),
        };
      });

      await updateExclusionsSurvey.mutateAsync({
        sections,
      });
      if (!appConfigData?.data) {
        setAreControlsDisabled(false);
        return;
      }

      await updateAppConfig.mutateAsync(
        { data: { ...appConfigData?.data, isExclusionSurveyUpToDate: true } },
        {
          onSuccess: () => {
            pushSuccessToast({
              message: 'Investor Exclusions survey is successfully updated.',
            });
          },
        }
      );
      setAreControlsDisabled(false);
    } catch (e) {
      pushErrorToast({ message: 'Failed to update the survey' });
      setAreControlsDisabled(false);
    }
  }, [
    appConfigData?.data,
    createExclusionsDbScreenshot,
    items,
    pushErrorToast,
    pushSuccessToast,
    updateAppConfig,
    updateExclusionsSurvey,
  ]);

  const onResetFromScreenshot = useCallback(async () => {
    setAreControlsDisabled(true);
    await resetFromScreenshot.mutateAsync();
    if (!appConfigData?.data) return;
    await updateAppConfig.mutateAsync({
      data: { ...appConfigData.data, isExclusionSurveyUpToDate: true },
    });
    pushSuccessToast({
      message:
        'All changes have been successfully discarded, and exclusions have been reverted to the previously applied state.',
    });
    setAreControlsDisabled(false);
  }, [appConfigData?.data, pushSuccessToast, resetFromScreenshot, updateAppConfig]);

  return { onUpdateSurvey, onResetFromScreenshot, areControlsDisabled };
};
